import React, { useState, useEffect, useRef } from 'react';
import { getUserProfile, setUserProfile } from '../utils/localStorage';
import './ProfileMenu.css';

const ProfileMenu = ({ isOpen, onClose }) => {
  const [profile, setProfile] = useState({ name: 'Guest', profilePic: null });
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const menuRef = useRef();

  useEffect(() => {
    const userProfile = getUserProfile();
    setProfile(userProfile);
    setNewName(userProfile.name);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedProfile = { ...profile, profilePic: reader.result };
        setProfile(updatedProfile);
        setUserProfile(updatedProfile);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNameSave = () => {
    if (newName.trim()) {
      const updatedProfile = { ...profile, name: newName.trim() };
      setProfile(updatedProfile);
      setUserProfile(updatedProfile);
      setIsEditing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-menu" ref={menuRef}>
      <div className="profile-menu-header">
        <div className="profile-pic-container">
          {profile.profilePic ? (
            <img src={profile.profilePic} alt={profile.name} className="profile-pic" />
          ) : (
            <div className="profile-pic-placeholder">
              {profile.name.charAt(0).toUpperCase()}
            </div>
          )}
          <label className="profile-pic-upload">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 4V1h2v3h3v2H5v3H3V6H0V4h3zm3 6V7h3V4h7l1.83 2H21c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V10h3zm7 9c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-3.2-5c0 1.77 1.43 3.2 3.2 3.2s3.2-1.43 3.2-3.2-1.43-3.2-3.2-3.2-3.2 1.43-3.2 3.2z"/>
            </svg>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
          </label>
        </div>
        
        <div className="profile-info">
          {isEditing ? (
            <div className="profile-edit">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter your name"
                className="profile-name-input"
                autoFocus
              />
              <div className="profile-edit-buttons">
                <button onClick={handleNameSave} className="btn-save">Save</button>
                <button onClick={() => setIsEditing(false)} className="btn-cancel">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="profile-name">{profile.name}</h3>
              <button onClick={() => setIsEditing(true)} className="btn-edit-name">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                Edit name
              </button>
            </>
          )}
        </div>
      </div>

      <div className="profile-menu-divider"></div>

      <div className="profile-menu-items">
        <button className="profile-menu-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span>My Channel</span>
        </button>

        <button className="profile-menu-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>
          </svg>
          <span>Settings</span>
        </button>

        <button className="profile-menu-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
          </svg>
          <span>Watch History</span>
        </button>

        <button className="profile-menu-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span>Favorite Channels</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileMenu;
